import React from 'react'
import { useState, useRef, useMemo, useEffect } from 'react'
import { OmDropdownMenu, type OmMenuItem } from '@owma/kit'
import styles from './style.module.scss'
import moment, { Moment } from 'moment'
import { useTranslation } from 'react-i18next'

import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import { v4 as uuidv4 } from 'uuid'

interface TimezoneInfo {
  tzId: string | null
  cityName: string
  searchtext: string
}

interface TimezonePickerProps {
  tzId: string | null
  focusDate: Date
  onTzChange: (tzId: string | null) => void
  forSettings?: boolean
  disabled?: boolean
}

const TimezonePicker: React.FC<TimezonePickerProps> = ({
  tzId,
  focusDate,
  onTzChange,
  forSettings = false,
  disabled = false,
}) => {
  const { t } = useTranslation()

  const [searchText, setSearchText] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const tzInputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const closePicker = (): void => {
    setSearchText('')
    setIsOpen(false)
  }

  // 콤보박스 닫힘: input blur 대신 "외부 pointerdown" 으로만 닫는다.
  //   dropdown 은 Radix Portal(body)로 떨어져 있어, 스크롤바 drag·내부 클릭·포커스 이동 시 input 이 blur 되면
  //   onBlur 방식은 스크롤바 drag 만 해도 닫혀버린다. picker root 나 .om-dropdown(=열린 메뉴) 내부가 아닌 곳을 눌렀을 때만 닫는다.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as HTMLElement | null
      if (target == null) return
      const inPicker = rootRef.current?.contains(target) ?? false
      const inDropdown = target.closest('.om-dropdown') != null
      if (!inPicker && !inDropdown) closePicker()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [isOpen])

  const allTimezoneInfo = useMemo<TimezoneInfo[]>(() => {
    const info = Intl.supportedValuesOf('timeZone').map((tz) => {
      /* i18next-extract-disable-next-line */
      const aCityName = t('timezone.' + tz)
      let cityName = aCityName
      if (aCityName === 'timezone.' + tz) {
        cityName = tz.split('/').pop() || tz
      }
      return {
        tzId: tz,
        cityName: cityName,
        searchtext: tz + ' ' + cityName,
      }
    })
    if (forSettings) {
      info.unshift({
        tzId: null,
        cityName: t('settings.accountTabs.none'),
        searchtext: t('settings.accountTabs.none'),
      })
    }
    return info
  }, [forSettings, t])

  const pickerId = useMemo<string>(() => {
    return uuidv4()
  }, [])

  const getTimezoneOffsetAndName = (tz: string | null, date: Date): string => {
    if (!tz) {
      if (forSettings) {
        return t('settings.accountTabs.none')
      } else {
        tz = 'Etc/UTC'
      }
    }

    const momentTz = moment(date).tz(tz)
    /* i18next-extract-disable-next-line */
    const aCityName = t('timezone.' + tz)
    let cityName = aCityName
    if (aCityName === 'timezone.' + tz) {
      cityName = tz.split('/').pop() || tz
    }

    return `(${getTZOffsetFromMoment(momentTz)}) ${cityName}`
  }

  const getTZOffsetFromMoment = (momentDate: Moment | null): string | null => {
    if (!momentDate) {
      return null
    }
    const offset = momentDate.format('Z')
    const isDST = momentDate.isDST()
    return `UTC${offset}${isDST ? '☀️' : ''}`
  }

  const getFilteredTimezoneOptions = (): OmMenuItem[] => {
    const momentDate = moment(focusDate)
    let timeZoneList = allTimezoneInfo
    if (searchText) {
      timeZoneList = timeZoneList.filter((tzInfo) =>
        tzInfo.searchtext.toLowerCase().includes(searchText.toLowerCase()),
      )
    }
    return timeZoneList
      .map((tzInfo) => {
        return {
          key: pickerId + ':' + tzInfo.tzId,
          offset: tzInfo.tzId ? momentDate.tz(tzInfo.tzId)?.utcOffset() : Number.MIN_SAFE_INTEGER,
          label: (
            <div className={styles.timezoneMenuItem}>
              <div className={styles.timezoneName}>
                {tzInfo.cityName +
                  (tzInfo.tzId
                    ? ' (' + getTZOffsetFromMoment(momentDate.tz(tzInfo.tzId)) + ')'
                    : '')}
              </div>
              <div className={styles.timezoneId}>{tzInfo.tzId}</div>
            </div>
          ),
          onClick: () => {
            onTzChange(tzInfo.tzId)
            closePicker()
          },
        }
      })
      .sort((a, b) => (a.offset as number) - (b.offset as number))
  }

  const renderTimezoneInput = (tzId: string | null): React.ReactNode => (
    <div className={styles.timezoneInputWrapper}>
      <input
        ref={tzInputRef}
        className={styles.timezoneInput}
        placeholder={getTimezoneOffsetAndName(tzId, focusDate)}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onFocus={() => {
          setIsOpen(true)
          setTimeout(() => {
            scrollToSelected()
          }, 100)
        }}
      />
      <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
    </div>
  )

  const scrollToSelected = (): void => {
    const selectedItem = document.querySelector(`[data-menu-id$="${pickerId}:${tzId}"]`)
    if (selectedItem) {
      setTimeout(() => {
        selectedItem.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        })
      }, 100)
    }
  }

  return (
    <div
      ref={rootRef}
      className={styles.timezonePickers}
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onClick={(e) => {
        if (tzInputRef.current && !isOpen) {
          tzInputRef.current.focus()
        } else {
          e.stopPropagation()
        }
      }}
    >
      <OmDropdownMenu
        open={isOpen}
        menu={{
          items: getFilteredTimezoneOptions(),
          selectedKeys: [pickerId + ':' + tzId],
          // 콤보박스: 드롭다운이 열릴 때 메뉴 UL 로 포커스가 이동하면 검색 input 이 blur 되어
          // onBlur 의 setIsOpen(false) 로 열자마자 닫힌다 → tabIndex:-1 로 포커스를 input 에 유지.
          tabIndex: -1,
        }}
        placement="bottomLeft"
        overlayClassName={styles.timezoneDropdown}
        destroyOnHidden={true}
      >
        {renderTimezoneInput(tzId)}
      </OmDropdownMenu>
    </div>
  )
}

export default TimezonePicker
